import type { StandardDetail } from '@/standards/standard_types'
import { forSetCodes } from '@/standards/set_code_map'

type HeaderFormat = (standard: StandardDetail) => string

const shortRefThen = (field: string): HeaderFormat => (standard) => `${standard.standard_short_ref} ${standard[field]}`
const shortRefColonThen = (field: string): HeaderFormat => (standard) => `${standard.standard_short_ref}: ${standard[field]}`
const longRefThen = (field: string): HeaderFormat => (standard) => `${standard.standard_long_ref} ${standard[field]}`
const shortRefThenOptional = (field: string): HeaderFormat => (standard) => `${standard.standard_short_ref} ${standard[field] || ''}`
const shortRefOnly: HeaderFormat = (standard) => `${standard.standard_short_ref}`

export function eldStandardLevelLabel(standard: StandardDetail): string {
  const proficiencyLevel = parseInt(String(standard.standard_num))
  if (proficiencyLevel < 2) return 'Beginner'
  if (proficiencyLevel === 2) return 'Intermediate'
  if (proficiencyLevel > 2) return 'Advanced'
  return ''
}

function ncscosHeader(standard: StandardDetail): string {
  if (standard.math_hs_course_strands) {
    return `${standard.standard_long_ref} ${standard.math_hs_course_strands}`
  }
  return `${standard.standard_long_ref} ${standard.strand_domain}`
}

function heldsHeader(standard: StandardDetail): string {
  if (standard.equivalent_standard_ref) {
    return `${standard.equivalent_standard_ref} ${standard.domain}`
  }
  return `HELDS.${standard.standard_short_ref} ${standard.domain}`
}

function mlsHeader(standard: StandardDetail): string {
  const domainHeader = `${standard.standard_short_ref} ${standard.domain_description}`
  if (standard.subject === 'Mathematics') {
    return domainHeader
  }
  return `${domainHeader}: ${standard.concept}`
}

function caPeHeader(standard: StandardDetail): string {
  const title = standard.cluster || standard.umbrella_title
  return `${standard.standard_short_ref} ${title}`
}

const HEADER_FORMAT_BY_SET_CODE: Record<string, HeaderFormat> = {
  ...forSetCodes(['CCSS', 'HCPS', 'NACS-PE', 'AELG', 'IELG'], shortRefThen('cluster')),
  ...forSetCodes(['DLM-EE', 'NCSC-CCC', 'TEKS.PCT', 'SC.CCR'], (standard) => standard.standard_short_ref),
  ...forSetCodes(
    ['NY-PreK', 'GELDS', 'AELS', 'CAS-EEO', 'NECCRS', 'OKAS', 'NV.PKS', 'OLS.SS', 'OLS.S', 'LA-ELDS', 'RIELDS', 'TN-ELDS', 'GSE.PE'],
    shortRefColonThen('strand')
  ),
  CAS: (standard) => `${standard.standard_short_ref} ${standard.strand ? `: ${standard.strand}` : ''}`,
  ...forSetCodes(['NC.SS', 'NCS', 'NDSSCS', 'ELDS', 'NY.PE', 'LA.PE', 'MN.PE', 'AL.SS'], shortRefThen('strand')),
  ...forSetCodes(
    ['LSS.LC', 'CPLF', 'VESOL', 'WIDA.ELD', 'NDELS', 'INELS', 'NYSS', 'ASELD', 'HEGSE', 'AAAS', 'TEKS-P', 'HFLS', 'VASOL', 'SC', 'NE-PE', 'NE-SS'],
    shortRefThen('subject')
  ),
  'NVACS-H': shortRefColonThen('subject'),
  ...forSetCodes(['TPKG', 'ND.CS'], (standard) => `${standard.standard_short_ref} ${standard.domain}: ${standard.cluster}`),
  ...forSetCodes(['NCSC.CCC', 'DLM.EE'], (standard) => `${standard.strand_domain} ${standard.standard_short_ref}`),
  'CA-ELD': (standard) => `${standard.strand}: ${eldStandardLevelLabel(standard)}`,
  'CA-PE': caPeHeader,
  'ILS-SEL': shortRefThen('ils_standard'),
  ETEKS: shortRefThen('cluster_description'),
  ...forSetCodes(['TEKS', 'TEKS-2017'], (standard) => `${standard.standard_short_ref}, Grade ${standard.grades_str}`),
  'CDC-AEM': (standard) => `${standard.standard_long_ref} ${standard.year}: ${standard.subject}`,
  NCSCoS: ncscosHeader,
  HELDS: heldsHeader,
  ...forSetCodes(['NYSNGLS', 'OLS', 'OLS-E', 'OLS-T'], longRefThen('strand_domain')),
  OELDS: longRefThen('domain'),
  'OLS-FL': longRefThen('topic'),
  'OLS-PE': longRefThen('benchmark_skill'),
  MLS: mlsHeader,
  'MS-AAAS': (standard) =>
    `${standard.standard_short_ref} ${standard.math_hs_course || ''} ${standard.hs_subject || ''} ${standard.strand_domain}`,
  ...forSetCodes(
    [
      'NJSLS', 'MN', 'MAS', 'AZ', 'LSS', 'GSE', 'GECS', 'MCF', 'MAELG', 'PCS', 'PEC', 'SCCCRP', 'SCCCR', 'TSS', 'KS', 'IAS',
      'INCC', 'MCCRS', 'WAAPF', 'NVACS', 'NVACSC', 'KY', 'FL', 'FLB', 'MAFS', 'LAFS', 'MS-CCRS', 'NC.ECS', 'IDCCC', 'NMCCSS',
      'NDCS', 'IC', 'WCSE', 'HSA-Alt', 'VTAA', 'OEAF', 'IELDS', 'UTCS', 'AKCS', 'WVCCRS', 'WVAAAS', 'OELALS', 'SDSS', 'WAS',
      'WIS', 'MELG', 'NCFELD', 'ISELC', 'WISEL', 'CELDG', 'KELS', 'MS-SEL', 'SECD', 'MELS', 'RICS', 'NJ.SLS', 'NDSCS',
      'SC-CCR', 'TS'
    ],
    shortRefThenOptional('strand_domain')
  ),
  'Alternate-K-PREP': shortRefThen('subject_domain'),
  PAEC: shortRefThen('strand_domain_text'),
  VSOL: shortRefThen('strand_description'),
  ...forSetCodes(['IDCS', 'WCPS', 'AAS'], (standard) =>
    `${standard.standard_short_ref} ${standard.math_hs_subject || ''} ${standard.strand_domain}`
  ),
  ACS: (standard) => `${standard.standard_short_ref} ${standard.strand_domain || standard.domain || ''}`,
  WELDG: shortRefThenOptional('strand_domain'),
  ...forSetCodes(
    [
      'ALAAS', 'IELF', 'WASEL', 'MDELS', 'WMELS', 'AZ.SEL', 'LELDS', 'OELKS', 'MN-SEL', 'MASM', 'MSCS', 'FELDS', 'NJELS',
      'TXELG', 'TEKS.PE', 'SDELG', 'WELS', 'DELF', 'OELG', 'ARELS', 'KYECS', 'NMELG', 'NCOS', 'AZELPS', 'DCELS', 'TPG',
      'ITELG', 'NV.ELG', 'FL.RES', 'ND.CTE', 'INAS', 'NGSS', 'CTELDS', 'ME-ELDS', 'NPKS', 'NDPECS', 'NVESCRS', 'NH-ELS',
      'CA-T-SEL', 'PTKLF', 'KSELA', 'USS-ELA', 'GSELA', 'KSECE', 'IDCS.ELAL', 'ECS', 'MD-ELS', 'SC-ELS', 'LSS-S', 'PLSEC',
      'MT-ELS', 'ELG', 'NECCRSEI', 'WCSDC', 'OMS', 'HCSSS', 'WLS', 'LSS-SS', 'MDE-SEL', 'MSSF', 'M-CCRS', 'MI.SCI', 'MI.SS',
      'FL.PE', 'SD.CCC', 'GA.SCI', 'GA.SS', 'AL.SCI', 'RI.PE', 'SC.ALT', 'CT.ELDS', 'MI.KENT.TS'
    ],
    shortRefThen('domain')
  ),
  ...forSetCodes(['ECIPs', 'RELDS', 'IELS'], (standard) => `${standard.standard_short_ref} ${standard.domain}: ${standard.strand}`),
  ...forSetCodes(['VELS', 'OH-SEL'], (standard) => `${standard.standard_short_ref} ${standard.level}: ${standard.domain}`),
  'TEKS.SS': shortRefThenOptional('hs_course'),
  ...forSetCodes(['TEKS.ELPS', 'NYS.SLS', 'TEKS.SCI', 'TEKS-SS', 'NHES', 'ELP', 'MD-PE', 'MI.PE', 'AL.PE'], shortRefOnly),
  'TEKS.CTE': shortRefThen('course'),
  ...forSetCodes(['OH.ELDS', 'GMS', 'NY.CDOS', 'NYPKELS', 'MELDS', 'NDES', 'SOL'], shortRefColonThen('domain')),
  'TEKS-SCI': (standard) =>
    `${standard.hs_course_code ? `${standard.hs_course_code}.` : ''}${standard.standard_short_ref} ${standard.strand}`,
  MLR: (standard) => `${standard.standard_short_ref} ${standard.strand}: ${standard.domain}`,
  HICCS: shortRefColonThen('subject_code'),
  'MLS.S': (standard) => `${standard.cluster}`,
  RISELS: (standard) => `${standard.domain} ${standard.standard_short_ref}`,
  'IAS-PE': (standard) => `${standard.standard_short_ref}${standard.domain ? ` ${standard.domain}` : ''}`,
  'MLS.SS': (standard) => `${standard.standard_short_ref}${standard.course}`
}

export function panelHeaderTextForStandard(standard: StandardDetail): string {
  if (!Object.hasOwn(HEADER_FORMAT_BY_SET_CODE, standard.standard_set_code)) {
    return standard.standard_long_ref
  }
  return HEADER_FORMAT_BY_SET_CODE[standard.standard_set_code](standard)
}
